import * as React from "react";
import { Audit } from "./types";
import ReactDiff from "react-diff-viewer";
import { X, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "react-feather";
import { getAuditSubject } from "./utils";
import jsYaml from "js-yaml";
import { request } from "../../services/cpln";
import { Button, Checkbox, notification, Popconfirm } from "antd";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setUseSplitViewComparison } from "../../store/uiDataSlice";

interface Props {
  audits: Audit[];
  onClose: () => void;
  refetch?: any;
  itemVersion: number;
  selfLink: string;
}
export const AuditTrailDiffView: React.FC<Props> = ({ audits, onClose, refetch, itemVersion, selfLink }) => {
  const dispatch = useAppDispatch();
  const theme = useAppSelector((state) => state.theme.theme);
  const useSplitViewComparison = useAppSelector((state) => state.uiData.useSplitViewComparison);

  const [isApplying, setIsApplying] = React.useState(-1);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const previousIndex = currentIndex + 1;

  function bringOlder() {
    setCurrentIndex(Math.min(currentIndex + 1, audits.length - 2));
  }

  function bringOldest() {
    setCurrentIndex(audits.length - 2);
  }

  function bringNewer() {
    setCurrentIndex(Math.max(currentIndex - 1, 0));
  }

  function bringNewest() {
    setCurrentIndex(0);
  }

  async function applyVersion(_data: any) {
    const data = JSON.parse(JSON.stringify(_data));
    const version = data?.version;

    delete data.id;
    delete data.version;
    delete data.links;
    delete data.created;
    delete data.lastModified;
    delete data.status;

    try {
      setIsApplying(version);
      await request({ method: "patch", url: selfLink, body: data });
      setIsApplying(-1);
      if (refetch) {
        refetch();
      }
    } catch (e) {
      setIsApplying(-1);
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({ message: "Failed to Apply Version", description: errorMessage });
    }
  }

  const canBringOlder = currentIndex < audits.length - 2;
  const canBringNewer = currentIndex > 0;
  const isOldest = currentIndex === audits.length - 2;
  const isNewest = currentIndex === 0;

  const currentAudit = audits[currentIndex];
  const current = currentAudit.resource.data ?? {};
  const previous = audits[previousIndex].resource.data ?? {};
  const subject = getAuditSubject(currentAudit);

  let title = "";
  if (previous.version && current.version) {
    title = `Version ${previous.version} - Version ${current.version}`;
  } else if (current.version) {
    title = `Version ${current.version}`;
  } else if (previous.version) {
    title = `Version ${previous.version}`;
  }

  return (
    <>
      <div className="flex items-center mb-4 gap-4">
        <Button type={"default"} danger className="basis-0 flex-grow" onClick={() => onClose()} icon={<X />}>
          Close Diff View
        </Button>
        <Button
          type={"primary"}
          className="basis-0 flex-grow"
          disabled={isOldest}
          onClick={bringOldest}
          icon={<ChevronsLeft />}
          iconPosition={"start"}
        >
          Oldest
        </Button>
        <Button
          type={"primary"}
          className="basis-0 flex-grow"
          disabled={!canBringOlder}
          onClick={bringOlder}
          icon={<ChevronLeft />}
          iconPosition={"start"}
        >
          Previous
        </Button>
        <Button
          type={"primary"}
          className="basis-0 flex-grow"
          disabled={!canBringNewer}
          onClick={bringNewer}
          icon={<ChevronRight />}
          iconPosition="end"
        >
          Next
        </Button>
        <Button
          type={"primary"}
          className="basis-0 flex-grow"
          disabled={isNewest}
          onClick={bringNewest}
          icon={<ChevronsRight />}
          iconPosition="end"
        >
          Newest
        </Button>
      </div>
      <div className="text-sm diff-container">
        <div className="flex justify-between mb-4 text-base text-normal">
          <div className="flex items-center gap-6">
            <div>{title}</div>
            <Checkbox
              checked={useSplitViewComparison}
              onChange={(e) => dispatch(setUseSplitViewComparison(e.target.checked))}
            >
              Use Split View
            </Checkbox>
            {!!previous.kind && previous.kind !== "secret" ? (
              <Popconfirm
                title={`Apply Version ${previous.version}`}
                description={`This action will override this object with a previous version. Are you sure you want to continue?`}
                onConfirm={() => applyVersion(previous)}
              >
                <Button
                  type={`default`}
                  size={`small`}
                  disabled={isApplying !== -1}
                  loading={isApplying === previous.version}
                >
                  Apply Version {previous.version}
                </Button>
              </Popconfirm>
            ) : null}
            {!!current.kind && current.kind !== "secret" && current.version !== itemVersion ? (
              <Popconfirm
                title={`Apply Version ${current.version}`}
                description={`This action will override this object with a previous version. Are you sure you want to continue?`}
                onConfirm={() => applyVersion(current)}
              >
                <Button
                  type={`default`}
                  size={`small`}
                  disabled={isApplying !== -1}
                  loading={isApplying === current.version}
                >
                  Apply Version {current.version}
                </Button>
              </Popconfirm>
            ) : null}
          </div>
          <div>{subject.value}</div>
        </div>
        <div className="border" style={{ overflow: "auto" }}>
          <ReactDiff
            splitView={useSplitViewComparison}
            oldValue={jsYaml.dump(previous, { indent: 2 })}
            newValue={jsYaml.dump(current, { indent: 2 })}
            useDarkTheme={theme === "dark"}
          />
        </div>
      </div>
    </>
  );
};
